/**
 * One place that decides how a service status is labelled and coloured.
 *
 * This lived inlined in four service lists (Ports, Internet, Cloud
 * Connectivity, Datacenter Interconnect), each a near-copy of the others, and
 * every copy carried the same hole: a final `else` that painted **anything
 * unrecognised** as "Warning". The backend answers `'unknown'` when it could
 * not read the telemetry, which is a string and therefore not the `null` those
 * branches checked for — so a collector outage turned every port amber, which
 * is the same lie as painting it red, just quieter.
 *
 * The rule now: we colour what we actually know. A status we do not recognise
 * is an absence of information, not evidence of a problem, so it reads as
 * "No data" rather than inventing a severity for it.
 */

export type ServiceStatusVariant = "success" | "error" | "warning" | "default";

export interface ServiceStatusTag {
    label: string;
    variant: ServiceStatusVariant;
}

const HEALTHY = new Set(["up", "active", "connected"]);
const FAILED = new Set(["down", "inactive", "disconnected"]);
/**
 * Degraded states the backend genuinely reports — `calculateFWAASBasedInternetStatus`
 * emits `degraded` and `partial_failure` alongside `warning`. These are real
 * findings and must keep their amber; they are the reason the old catch-all
 * existed at all.
 */
const DEGRADED = new Set(["warning", "degraded", "partial_failure"]);

export const NO_DATA_TAG: ServiceStatusTag = {
    label: "No data",
    variant: "default",
};

function normalize(status?: string | null): string {
    return (status ?? "").toString().trim().toLowerCase();
}

export function resolveServiceStatusTag(
    status?: string | null
): ServiceStatusTag {
    const value = normalize(status);

    if (HEALTHY.has(value)) return { label: "Active", variant: "success" };
    if (FAILED.has(value)) return { label: "Down", variant: "error" };
    if (DEGRADED.has(value)) return { label: "Warning", variant: "warning" };

    // Covers 'unknown', null, undefined, '' and anything new the backend
    // starts sending before this list learns about it.
    return NO_DATA_TAG;
}

/**
 * Sort weight, so a column sorts Active > Warning > Down > No data. Kept here
 * with the labels because the two have to agree: sorting by a rank that
 * disagreed with the badge is how a table stops making sense.
 */
export function serviceStatusRank(status?: string | null): number {
    const value = normalize(status);

    if (HEALTHY.has(value)) return 3;
    if (DEGRADED.has(value)) return 2;
    if (FAILED.has(value)) return 1;
    return 0;
}
